import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableImage, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import PageMeta from "@/components/PageMeta";
import {
  Sparkles,
  Heart,
  ArrowRight,
  CheckCircle2,
  Eye,
  MessageCircle,
  Clock,
  Image,
  Palette,
  DollarSign,
  Instagram,
  Upload,
  Smile,
  Moon,
  VolumeX,
  Scissors,
  Layers,
  Crown,
  EyeOff,
  Calendar,
  MapPin,
  Send,
  Bell,
  Check,
  Link as LinkIcon,
  Download,
  Phone,
  MessageSquare,
  Share,
  Lock,
  Unlock,
  Settings,
  ShoppingCart,
  AlertCircle,
  Info,
  Droplets,
  Package,
  Paintbrush,
  Wine,
  ShoppingBag
} from "lucide-react";

const HairSalesPage = () => {
  const [showPrices, setShowPrices] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState("knotless");
  const [storyShowPrices, setStoryShowPrices] = useState(true);
  const [promoText, setPromoText] = useState("");
  const [bookingStep, setBookingStep] = useState(1);
  const [hairOption, setHairOption] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Hair Stylists";
  }, []);

  // Hair stylist services with variations AND what's needed
  const hairServices = {
    knotless: {
      name: "Knotless Braids",
      basePrice: "$180+",
      icon: "✨",
      addOns: [
        { name: "Length", options: ["Bob/Shoulder", "Mid Back", "Lower Back", "Below Butt"], prices: ["+$0", "+$30", "+$60", "+$100"] },
        { name: "Style", options: ["Classic", "Boho"], prices: ["+$0", "+$40"] },
        { name: "Size", options: ["Medium", "Large"], prices: ["+$0", "+$0"] },
        { name: "Parts", options: ["Standard", "Extra Large Parts"], prices: ["+$0", "+$25"] }
      ],
      hairNeeded: {
        type: "Pre-stretched braiding hair",
        packs: "7-9 packs",
        recommended: ["X-Pression", "Outre", "FreeTress"],
        buyLinks: [
          { name: "Amazon", url: "#" },
          { name: "Beauty Supply", url: "#" }
        ],
        provideOption: { available: true, price: "+$50" }
      },
      reminders: [
        { text: "Come with hair freshly washed & dried", required: true },
        { text: "No product in hair", required: true },
        { text: "Detangled and blow-dried preferred", required: false }
      ]
    },
    cornrows: {
      name: "Corn Rows",
      basePrice: "$80+",
      icon: "〰️",
      options: [
        { count: "2 rows", price: "$80" },
        { count: "4 rows", price: "$100" },
        { count: "6 rows", price: "$120" },
        { count: "8 rows", price: "$140" }
      ],
      hairNeeded: null,
      reminders: [
        { text: "Come with hair freshly washed & dried", required: true },
        { text: "Natural hair only - no extensions needed", required: false }
      ]
    },
    lemonade: {
      name: "Lemonade Braids",
      basePrice: "$200",
      icon: "🍋",
      desc: "20 side-swept braids",
      hairNeeded: {
        type: "Pre-stretched braiding hair",
        packs: "5-7 packs",
        recommended: ["X-Pression", "Outre"],
        buyLinks: [
          { name: "Amazon", url: "#" },
          { name: "Beauty Supply", url: "#" }
        ],
        provideOption: { available: true, price: "+$40" }
      },
      reminders: [
        { text: "Come with hair freshly washed & dried", required: true },
        { text: "No gel or product", required: true }
      ]
    },
    tribal: {
      name: "Tribal Braids",
      basePrice: "$220+",
      icon: "🌍",
      options: [
        { count: "8-10 braids", price: "$220" },
        { count: "10-12 braids", price: "$250" },
        { count: "14-18 braids", price: "$300" }
      ],
      hairNeeded: {
        type: "Pre-stretched braiding hair",
        packs: "4-6 packs",
        recommended: ["X-Pression", "FreeTress"],
        buyLinks: [
          { name: "Amazon", url: "#" },
          { name: "Beauty Supply", url: "#" }
        ],
        provideOption: { available: true, price: "+$35" }
      },
      reminders: [
        { text: "Come with hair freshly washed & dried", required: true }
      ]
    },
    quickweave: {
      name: "Quick Weaves",
      basePrice: "$150+",
      icon: "💫",
      desc: "Fast install, versatile styles",
      hairNeeded: {
        type: "Bundles (body wave, straight, etc.)",
        packs: "2-3 bundles + closure",
        recommended: ["Any quality bundles"],
        buyLinks: [
          { name: "Amazon", url: "#" },
          { name: "Beauty Supply", url: "#" }
        ],
        provideOption: { available: true, price: "+$80" }
      },
      reminders: [
        { text: "Come with hair freshly washed & dried", required: true },
        { text: "Stocking cap needed (or we provide)", required: false }
      ]
    }
  };

  // Service presets (photos for each style)
  const servicePresets = {
    knotless: [
      { id: 1, name: "Classic Medium", popular: true },
      { id: 2, name: "Boho Style", popular: true },
      { id: 3, name: "Jumbo", popular: false },
      { id: 4, name: "Bob Length", popular: false },
    ],
    cornrows: [
      { id: 1, name: "Feed-in 4", popular: true },
      { id: 2, name: "Stitch Braids", popular: false },
    ],
    tribal: [
      { id: 1, name: "With Curls", popular: true },
      { id: 2, name: "Straight Ends", popular: false },
    ],
    lemonade: [
      { id: 1, name: "Side Swept", popular: true },
    ],
    quickweave: [
      { id: 1, name: "Body Wave", popular: true },
      { id: 2, name: "Straight", popular: false },
    ]
  };

  // Client preference options
  const preferenceOptions = {
    edges: [
      { value: "none", label: "No edges", desc: "Natural look" },
      { value: "standard", label: "Edges", desc: "Clean finish" },
      { value: "dramatic", label: "Dramatic edges", desc: "Bold baby hairs" }
    ],
    length: [
      { value: "shoulder", label: "Shoulder length" },
      { value: "midback", label: "Mid back" },
      { value: "lowerback", label: "Lower back" },
      { value: "belowbutt", label: "Below the butt" }
    ],
    curls: [
      { value: "none", label: "No curls", desc: "Straight ends" },
      { value: "curls", label: "Curls at the end", desc: "Bouncy finish" }
    ],
    shade: [
      { value: "1", label: "1 (Jet Black)", desc: "Darkest black" },
      { value: "1b", label: "1B (Off Black)", desc: "Natural black" },
      { value: "2", label: "2 (Darkest Brown)", desc: "Nearly black brown" },
      { value: "2b", label: "2B (Dark Brown)", desc: "Rich dark brown" },
      { value: "4", label: "4 (Medium Brown)", desc: "Chocolate brown" },
      { value: "27", label: "27 (Honey Blonde)", desc: "Warm blonde" },
      { value: "30", label: "30 (Light Auburn)", desc: "Reddish brown" },
      { value: "33", label: "33 (Dark Auburn)", desc: "Deep red brown" },
      { value: "ombre", label: "Ombré", desc: "Gradient blend" },
      { value: "custom", label: "Custom Color", desc: "Specify your shade" }
    ],
    vibe: [
      { value: "chat", label: "Chat away", icon: Smile, desc: "Love conversation" },
      { value: "quiet", label: "Quiet please", icon: VolumeX, desc: "Prefer silence" },
      { value: "little", label: "Little chat", icon: MessageCircle, desc: "Some conversation" },
      { value: "tired", label: "Tired (sleep ok)", icon: Moon, desc: "May doze off" }
    ]
  };

  // Booking flow steps
  const bookingSteps = [
    { num: 1, title: "Client DMs You", desc: "Find you on IG, text or DM to book", icon: Instagram },
    { num: 2, title: "Send Preference Link", desc: "Create booking, send phygital link", icon: LinkIcon },
    { num: 3, title: "Client Fills Preferences", desc: "Style, length, vibe - all captured", icon: Heart },
    { num: 4, title: "You Confirm & Price", desc: "Review, set final price in app", icon: DollarSign },
    { num: 5, title: "Reminder Sent", desc: "Day before: client confirms", icon: Bell },
    { num: 6, title: "Address Released", desc: "Location sent after confirmation", icon: MapPin }
  ];

  const currentService = hairServices[selectedStyle];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta 
        title="Phygital for Hair Stylists - Know Their Vision Before They Arrive"
        description="Collect style inspiration, hair history, and preferences before clients arrive. Built for salons, barbers, and independent stylists."
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="hidden sm:flex gap-1 bg-purple-100 text-purple-700">
              <Eye className="w-3 h-3" />
              Demo Mode
            </Badge>
            <Button 
              size="sm" 
              className="rounded-full"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="hair-early-access-btn"
            >
              Get Early Access
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <EditableSection sectionId="hair-hero" label="Hair Hero" className={editMode ? "pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 relative overflow-hidden" : "pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 relative overflow-hidden"}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-indigo-50/30" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-0">
            <Scissors className="w-3 h-3 mr-1" />
            <EditableText sectionId="hair-hero" field="badge" defaultValue="Hair Pilot by Phygital" className="inline" />
          </Badge>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            <EditableText sectionId="hair-hero" field="title_line1" defaultValue="Styles Booked" className="inline" /><br />
            <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-violet-500 bg-clip-text text-transparent">
              <EditableText sectionId="hair-hero" field="title_line2" defaultValue="Seamlessly" className="inline" />
            </span>
          </h1>
          
          <p className="text-sm sm:text-xl text-gray-700 max-w-3xl mx-auto mb-6 sm:mb-10 px-4">
            <EditableText 
              sectionId="hair-hero" 
              field="subtitle" 
              defaultValue="Clients find you on IG, DM to book. You send them a link, they fill their preferences, you confirm the price. Address released only after they confirm." 
              multiline
            />
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8 sm:mb-12">
            <Button 
              size="lg" 
              className="rounded-full bg-gray-900 hover:bg-gray-800 text-sm sm:text-base px-6 sm:px-8"
              onClick={() => document.getElementById('flow').scrollIntoView({ behavior: 'smooth' })}
            >
              See the Flow
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="rounded-full text-sm sm:text-base px-6 sm:px-8"
              onClick={() => document.getElementById('story-generator').scrollIntoView({ behavior: 'smooth' })}
            >
              <Instagram className="w-4 h-4 mr-2" />
              IG Story Generator
            </Button>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-6 text-[11px] sm:text-sm max-w-[340px] sm:max-w-none mx-auto">
            {[
              { icon: Instagram, text: "IG to Booked" },
              { icon: Calendar, text: "iCloud sync" },
              { icon: MapPin, text: "Address after confirm" },
              { icon: Share, text: "Story generator" }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 text-gray-700 whitespace-nowrap">
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Booking Flow - FROM DM TO DONE */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50" id="flow">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <MessageSquare className="w-3 h-3 mr-1" />
              Your Booking Flow
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              From DM to Done
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              No booking platform needed. Clients text or DM you, you handle the rest.
            </p>
          </div>

          {/* Flow Steps */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {bookingSteps.map((step, i) => (
              <div 
                key={i}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  bookingStep === step.num 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 bg-white hover:border-purple-200'
                }`}
                onClick={() => setBookingStep(step.num)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  bookingStep === step.num ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <step.icon className="w-5 h-5" />
                </div>
                <div className={`text-xs font-bold mb-1 ${bookingStep === step.num ? 'text-purple-600' : 'text-gray-500'}`}>
                  Step {step.num}
                </div>
                <h4 className="font-semibold text-sm text-gray-900">{step.title}</h4>
                <p className="text-xs text-gray-600 mt-1">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Flow Visualization */}
          <Card className="border-2 border-purple-200 overflow-hidden">
            <CardContent className="p-0">
              {bookingStep === 1 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Client Finds You on IG</h3>
                      <p className="text-xs text-gray-600">They slide into your DMs or text you</p>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-4 text-white max-w-sm mx-auto">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                      <div className="w-8 h-8 rounded-full bg-purple-500" />
                      <span className="text-sm font-medium">Maya</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="bg-white/10 rounded-2xl rounded-bl-none p-3 max-w-[80%]">
                        Hey! I saw your knotless braids and I'm obsessed
                      </div>
                      <div className="bg-white/10 rounded-2xl rounded-bl-none p-3 max-w-[80%]">
                        Do you have any openings this weekend?
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 mt-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>Text</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Instagram className="w-4 h-4" />
                      <span>DM</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MessageSquare className="w-4 h-4" />
                      <span>iMessage</span>
                    </div>
                  </div>
                </div>
              )}

              {bookingStep === 2 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                      <LinkIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Create Booking & Send Link</h3>
                      <p className="text-xs text-gray-600">You schedule in iCloud, send them the phygital link</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-5 h-5 text-purple-500" />
                        <span className="font-medium text-sm">Your iCloud Calendar</span>
                      </div>
                      <div className="bg-white p-3 rounded-lg border">
                        <p className="text-xs text-gray-600">Saturday, Jan 25</p>
                        <p className="font-medium">Maya - Knotless Braids</p>
                        <p className="text-sm text-gray-700">10:00 AM - 4:00 PM</p>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        Auto syncs with your calendar
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Send className="w-5 h-5 text-purple-500" />
                        <span className="font-medium text-sm">Send Preference Link</span>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-purple-200">
                        <p className="text-xs text-purple-600 mb-1">Your link:</p>
                        <code className="text-xs bg-purple-100 px-2 py-1 rounded block truncate">
                          phygital.systems/b/maya-jan25
                        </code>
                      </div>
                      <Button size="sm" className="w-full mt-3 bg-purple-500 hover:bg-purple-600">
                        <Send className="w-3 h-3 mr-1" />
                        Send to Maya
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {bookingStep === 3 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Client Fills Preferences</h3>
                      <p className="text-xs text-gray-600">Style, length, edges, vibe, everything captured</p>
                    </div>
                  </div>
                  <Card className="max-w-sm mx-auto border-2 border-pink-200">
                    <CardHeader className="bg-gradient-to-r from-pink-500 to-purple-500 text-white py-3 rounded-t-lg">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Your Appointment Preferences
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Style</p>
                        <p className="text-sm font-medium">Knotless Braids, Boho</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-600">Length</p>
                          <p className="text-sm font-medium">Lower back</p>
                        </div>
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-600">Edges</p>
                          <p className="text-sm font-medium">Dramatic</p>
                        </div>
                      </div>
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <p className="text-xs text-purple-600">Vibe</p>
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium">Tired, might sleep</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {bookingStep === 4 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">You Review & Set Final Price</h3>
                      <p className="text-xs text-gray-600">See preferences, confirm the total in app</p>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-4 text-white max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center font-bold">M</div>
                        <div>
                          <p className="font-semibold">Maya's Booking</p>
                          <p className="text-xs text-gray-500">Sat Jan 25 @ 10am</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-500">Pending Price</Badge>
                    </div>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Knotless Braids (base)</span>
                        <span>$180</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">+ Lower back length</span>
                        <span>$60</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">+ Boho style</span>
                        <span>$40</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-white/10 font-bold">
                        <span>Total</span>
                        <span className="text-green-400">$280</span>
                      </div>
                    </div>
                    <Button className="w-full bg-green-500 hover:bg-green-600">
                      <Check className="w-4 h-4 mr-1" />
                      Confirm Price & Send to Maya
                    </Button>
                  </div>
                </div>
              )}

              {bookingStep === 5 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <Bell className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Reminder Sent Day Before</h3>
                      <p className="text-xs text-gray-600">Client confirms to release appointment address</p>
                    </div>
                  </div>
                  <div className="max-w-sm mx-auto">
                    <div className="bg-gray-100 rounded-2xl p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium text-sm">phygital</span>
                        <span className="text-xs text-gray-500">now</span>
                      </div>
                      <p className="text-sm mb-3">
                        <strong>Reminder:</strong> Your appointment is tomorrow at 10am!
                      </p>
                      <p className="text-sm text-gray-700 mb-3">
                        Please confirm to receive the appointment location.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-green-500 hover:bg-green-600">
                          <Check className="w-3 h-3 mr-1" />
                          Confirm
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1">
                          Reschedule
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-amber-600 text-sm">
                      <Lock className="w-4 h-4" />
                      <span>Address hidden until confirmed</span>
                    </div>
                  </div>
                </div>
              )}

              {bookingStep === 6 && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Address Released</h3>
                      <p className="text-xs text-gray-600">Client confirmed, location now visible</p>
                    </div>
                  </div>
                  <div className="max-w-sm mx-auto">
                    <Card className="border-2 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Unlock className="w-5 h-5 text-green-500" />
                          <span className="text-green-700 font-medium">Appointment Confirmed!</span>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg mb-3">
                          <p className="text-xs text-green-600 mb-1">Appointment Location</p>
                          <p className="font-medium text-gray-900">1234 Braids Lane, Suite 5</p>
                          <p className="text-sm text-gray-700">Los Angeles, CA 90001</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button size="sm" variant="outline">
                            <MapPin className="w-3 h-3 mr-1" />
                            Directions
                          </Button>
                          <Button size="sm" variant="outline">
                            <Calendar className="w-3 h-3 mr-1" />
                            Add to Calendar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    <p className="text-center text-xs text-gray-600 mt-3">
                      Also synced to your iCloud calendar
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Service Menu with Hair Requirements */}
      <section className="py-12 sm:py-16 px-4" id="services">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Layers className="w-3 h-3 mr-1" />
              Service Menu
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Select a Style
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Browse styles, see preset looks, customize your options, and get what you need to bring.
            </p>
          </div>

          {/* Price Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8 p-4 bg-amber-50 rounded-xl border border-amber-200 max-w-md mx-auto">
            <EyeOff className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">Show prices to clients?</span>
            <Switch 
              checked={showPrices} 
              onCheckedChange={setShowPrices}
              className="data-[state=checked]:bg-purple-500"
            />
            <span className="text-xs text-amber-600">
              {showPrices ? "Yes" : "Quote after preferences"}
            </span>
          </div>

          {/* Style Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {Object.entries(hairServices).map(([key, service]) => (
              <Button
                key={key}
                variant={selectedStyle === key ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${selectedStyle === key ? 'bg-purple-500' : ''}`}
                onClick={() => { setSelectedStyle(key); setSelectedPreset(null); }}
              >
                <span className="mr-1">{service.icon}</span>
                {service.name}
              </Button>
            ))}
          </div>

          {/* Service Card with Presets */}
          <Card className="border-2 border-purple-200 mb-6">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="text-2xl">{currentService.icon}</span>
                  {currentService.name}
                </span>
                {showPrices && (
                  <Badge className="bg-white/20 text-white">
                    {currentService.basePrice}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Preset Photos */}
              {servicePresets[selectedStyle] && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Select a Look</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {servicePresets[selectedStyle].map((preset) => (
                      <div 
                        key={preset.id}
                        className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                          selectedPreset === preset.id 
                            ? 'border-purple-500 ring-2 ring-purple-200' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => setSelectedPreset(preset.id)}
                      >
                        <div className="aspect-square bg-gradient-to-br from-purple-100 to-indigo-100 relative">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Scissors className="w-8 h-8 text-purple-300" />
                          </div>
                          {preset.popular && (
                            <Badge className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] px-1.5 py-0">
                              Popular
                            </Badge>
                          )}
                          {selectedPreset === preset.id && (
                            <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                              <CheckCircle2 className="w-8 h-8 text-purple-500" />
                            </div>
                          )}
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs font-medium text-center">{preset.name}</p>
                        </div>
                      </div>
                    ))}
                    <div className="cursor-pointer rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-purple-300">
                      <div className="aspect-square bg-gray-50 flex flex-col items-center justify-center">
                        <Upload className="w-6 h-6 text-gray-500 mb-1" />
                        <span className="text-xs text-gray-500">Add Inspo</span>
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs font-medium text-center text-gray-500">Custom</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Customization Options */}
              <div className="space-y-4">
                {/* Add-ons for Knotless */}
                {selectedStyle === "knotless" && currentService.addOns && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Customize</p>
                    {currentService.addOns.map((addon, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs font-medium text-gray-600 mb-2">{addon.name}</p>
                        <div className="flex flex-wrap gap-2">
                          {addon.options.map((opt, j) => (
                            <Badge key={j} variant="secondary" className="text-xs cursor-pointer hover:bg-purple-100">
                              {opt}
                              {showPrices && addon.prices[j] !== "+$0" && (
                                <span className="ml-1 text-purple-600">{addon.prices[j]}</span>
                              )}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Options for cornrows/tribal */}
                {currentService.options && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Select Option</p>
                    <div className="grid grid-cols-2 gap-2">
                      {currentService.options.map((opt, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-xl text-center cursor-pointer hover:bg-purple-50 border-2 border-transparent hover:border-purple-200">
                          <p className="font-medium text-gray-900">{opt.count}</p>
                          {showPrices && <p className="text-purple-600 text-sm">{opt.price}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Simple description */}
                {currentService.desc && !currentService.options && (
                  <div className="text-center py-2">
                    <p className="text-gray-700">{currentService.desc}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Hair Requirements & Reminders - Side by Side */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* What Hair is Needed */}
            {currentService.hairNeeded && (
              <Card className="border-2 border-amber-200">
                <CardHeader className="bg-amber-50 py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    What You Need to Bring
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Hair Type</p>
                    <p className="text-sm text-gray-700">{currentService.hairNeeded.type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Amount</p>
                    <p className="text-sm text-gray-700">{currentService.hairNeeded.packs}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Recommended Brands</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {currentService.hairNeeded.recommended.map((brand, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{brand}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Quick Buy Links */}
                  <div className="flex gap-2">
                    {currentService.hairNeeded.buyLinks.map((link, i) => (
                      <Button key={i} variant="outline" size="sm" className="flex-1 text-xs">
                        <LinkIcon className="w-3 h-3 mr-1" />
                        {link.name}
                      </Button>
                    ))}
                  </div>

                  {/* Provide It For Me Option */}
                  {currentService.hairNeeded.provideOption?.available && (
                    <div 
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        hairOption === 'provide' 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => setHairOption(hairOption === 'provide' ? null : 'provide')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-sm">Provide It For Me</span>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          {currentService.hairNeeded.provideOption.price}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Stylist will have quality hair ready for you
                      </p>
                      {hairOption === 'provide' && (
                        <div className="mt-2 flex items-center gap-1 text-purple-600 text-xs">
                          <CheckCircle2 className="w-3 h-3" />
                          Selected
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reminders */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50 py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  Before Your Appointment
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {currentService.reminders.map((reminder, i) => (
                    <div key={i} className="flex items-start gap-2">
                      {reminder.required ? (
                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="text-sm">{reminder.text}</p>
                        {reminder.required && (
                          <p className="text-xs text-blue-600">Required</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Wash Service Option */}
                <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">Hair Wash Service</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">+$25</Badge>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Can't wash beforehand? We offer wash service
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Instagram Story Generator */}
      <section className="py-12 sm:py-16 px-4" id="story-generator">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 text-white border-0">
              <Instagram className="w-3 h-3 mr-1" />
              Story Generator
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Share Your Menu on IG Stories
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Generate a beautiful story image with your services. Toggle prices, add promos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Controls */}
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-500" />
                  Customize Your Story
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Price Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium">Show Prices</span>
                  </div>
                  <Switch 
                    checked={storyShowPrices} 
                    onCheckedChange={setStoryShowPrices}
                    className="data-[state=checked]:bg-purple-500"
                  />
                </div>

                {/* Promo Text */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Add a Promo (optional)
                  </label>
                  <Input 
                    placeholder="e.g., 20% off for new clients!"
                    value={promoText}
                    onChange={(e) => setPromoText(e.target.value)}
                    className="border-purple-200 focus:border-purple-500"
                  />
                </div>

                {/* Services to include */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Services to Feature
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(hairServices).map(([key, service]) => (
                      <Badge 
                        key={key}
                        variant="secondary"
                        className="cursor-pointer hover:bg-purple-100"
                      >
                        {service.icon} {service.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 hover:opacity-90">
                  <Download className="w-4 h-4 mr-2" />
                  Download Story Image
                </Button>
                <p className="text-xs text-gray-600 text-center">
                  1080x1920px • Ready for IG Stories
                </p>
              </CardContent>
            </Card>

            {/* Preview */}
            <div className="flex justify-center">
              <div className="w-[270px] h-[480px] bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-3xl p-4 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
                  <div className="absolute bottom-20 right-5 w-40 h-40 bg-white rounded-full blur-3xl" />
                </div>
                
                <div className="relative z-10 h-full flex flex-col">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-2">
                      <Scissors className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-lg">Current Services</h3>
                    {promoText && (
                      <Badge className="mt-2 bg-yellow-400 text-yellow-900 text-xs">
                        {promoText}
                      </Badge>
                    )}
                  </div>

                  <div className="flex-1 space-y-2 overflow-hidden">
                    {Object.entries(hairServices).slice(0, 4).map(([key, service]) => (
                      <div key={key} className="bg-white/20 backdrop-blur rounded-xl p-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>{service.icon}</span>
                            <span className="text-white text-xs font-medium">{service.name}</span>
                          </div>
                          {storyShowPrices && (
                            <span className="text-white/90 text-xs font-bold">{service.basePrice}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 text-center">
                    <p className="text-white/80 text-xs mb-2">DM to book</p>
                    <div className="flex items-center justify-center gap-1 text-white text-xs">
                      <PhygitalIcon variant="light" className="w-4 h-4" />
                      <span>phygital.systems</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Preferences */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Heart className="w-3 h-3 mr-1" />
              Client Preferences
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Know Exactly What They Want
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Style Preferences */}
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Scissors className="w-5 h-5 text-purple-500" />
                  Style Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Edges</p>
                  <div className="flex flex-wrap gap-2">
                    {preferenceOptions.edges.map((opt, i) => (
                      <Badge 
                        key={i} 
                        variant={i === 2 ? "default" : "secondary"}
                        className={i === 2 ? "bg-purple-500" : ""}
                      >
                        {opt.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Length</p>
                  <div className="flex flex-wrap gap-2">
                    {preferenceOptions.length.map((opt, i) => (
                      <Badge 
                        key={i} 
                        variant={i === 2 ? "default" : "secondary"}
                        className={i === 2 ? "bg-purple-500" : ""}
                      >
                        {opt.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ends</p>
                  <div className="flex flex-wrap gap-2">
                    {preferenceOptions.curls.map((opt, i) => (
                      <Badge 
                        key={i} 
                        variant={i === 1 ? "default" : "secondary"}
                        className={i === 1 ? "bg-purple-500" : ""}
                      >
                        {opt.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Shade</p>
                  <div className="flex flex-wrap gap-2">
                    {preferenceOptions.shade.slice(0, 6).map((opt, i) => (
                      <Badge 
                        key={i} 
                        variant={i === 1 ? "default" : "secondary"}
                        className={i === 1 ? "bg-gray-900 text-white" : ""}
                      >
                        {opt.label}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Black/African hair shades: 1, 1B, 2, 2B, 4, 27, 30, 33</p>
                </div>
              </CardContent>
            </Card>

            {/* Vibe Preferences */}
            <Card className="border-2 border-indigo-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-indigo-500" />
                  Vibe Preferences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {preferenceOptions.vibe.map((opt, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded-xl flex items-center gap-3 ${
                        i === 3 ? 'bg-indigo-100 border-2 border-indigo-300' : 'bg-gray-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        i === 3 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <opt.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${i === 3 ? 'text-indigo-800' : 'text-gray-700'}`}>
                          {opt.label}
                        </p>
                        <p className="text-xs text-gray-600">{opt.desc}</p>
                      </div>
                      {i === 3 && <CheckCircle2 className="w-5 h-5 text-indigo-500 ml-auto" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pro Features */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-white/20 text-white border-0">
              <Crown className="w-3 h-3 mr-1" />
              Pro Plan Features
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Make It Yours
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-500 flex items-center justify-center">
                    <Palette className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Custom Colors</h3>
                    <p className="text-white/60 text-sm">Match your brand</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-12 h-12 rounded-full bg-pink-500 border-4 border-white shadow-lg" />
                  <div className="w-12 h-12 rounded-full bg-purple-600 border-4 border-white/50" />
                  <div className="w-12 h-12 rounded-full bg-indigo-500 border-4 border-white/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center">
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Your Logo</h3>
                    <p className="text-white/60 text-sm">On stories & booking pages</p>
                  </div>
                </div>
                <div className="p-4 bg-white/10 rounded-xl border-2 border-dashed border-white/30 text-center">
                  <Upload className="w-6 h-6 text-white/60 mx-auto" />
                  <p className="text-white/60 text-sm mt-2">Upload your logo</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4">
            Ready to Streamline Your Bookings?
          </h2>
          <p className="text-gray-500 mb-8 text-sm sm:text-base">
            Join the pilot. DM to booked, seamlessly.
          </p>
          <Button 
            size="lg" 
            className="rounded-full bg-purple-500 hover:bg-purple-600"
            onClick={() => setShowEarlyAccessModal(true)}
            data-testid="hair-cta-early-access-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="hair" /> */}

      {/* Footer */}
      <SharedFooter variant="dark" />
      
      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} category="hair" />
    </div>
  );
};

export default HairSalesPage;
